import type { Distribution, Fabric, Project, Block } from "./state"
import { findFabricByRole } from "./state"
import { renderBlock, hexToValueHex, toValueColors } from "./block_svg"
import { assertNever, blockFinishedInches, getSchema, defaultPieceRolesFor, DEFAULT_BLOCK_STYLE, type BlockSchema } from "./blockschema"
import { DEFAULT_COLORWAY } from "./defaults"

// One cell of a layout grid: a colorway group plus a quarter-turn rotation
// applied to the painted block. Rotation 0 = upright; 1 = 90° clockwise;
// 2 = 180°; 3 = 270°. Cut math is unaffected — rotations are visual only —
// but for non-square blocks the rotated cell flips its painted bounds, so
// renderers must size the cell box from `(blockW, blockH)` swapped on odd
// quarter-turns. `schemaName` names which block schema to render for this cell;
// normally `project.block.style`, but can differ for alternating-block layouts.
export interface LayoutCell {
  group: string
  rotation: number  // 0 | 1 | 2 | 3
  schemaName: string
}

const GROUP_COLORS: Record<string, string> = {
  A: "#c4956a", B: "#8fb5b0", C: "#d4a8c7", D: "#b5c98e",
}

// Group A IS the default colorway — same hexes the swatch UI promises in
// block_svg.ts. B/C/D layer distinguishing palettes on top, and keep the
// quilt-render's lighter "#555" stroke. Without unifying group A, the swatch
// row above a rendered block disagreed with the rendered quilt below it
// whenever colorways was empty (LOOM_012).
const GROUP_BLOCK_COLORS: Record<string, Record<string, string>> = {
  A: DEFAULT_COLORWAY,
  B: { center: "#1d5a55", background: "#8fb5b0", points: "#d0eeeb", light: "#d0e8e4", dark: "#1a4a44", chain: "#1d5a55", bow: "#1d5a55", stroke: "#555" },
  C: { center: "#7a3870", background: "#d4a8c7", points: "#f5e2ee", light: "#f0d8ee", dark: "#7a2858", chain: "#7a3870", bow: "#7a3870", stroke: "#555" },
  D: { center: "#3a6020", background: "#b5c98e", points: "#dff0c2", light: "#e0f0c8", dark: "#2a5818", chain: "#3a6020", bow: "#3a6020", stroke: "#555" },
}

const FALLBACK_COLORS = DEFAULT_COLORWAY

const SWATCH_SASHING     = "#d4c4a0"
const SWATCH_CORNERSTONE = "#b8a880"

// Groups in fabric insertion order. When no fabrics carry groups, falls
// back to (1) sorted colorway keys, then (2) a synthetic ["A"] so every
// render path always has at least one group to paint with schema defaults.
// Without these fallbacks `fullQuiltSvg` produced an empty container any
// time the user picked a layout before adding fabrics (LOOM_010).
export function orderedGroups(project: Project): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const f of project.fabrics) {
    if (f.group && !seen.has(f.group)) {
      seen.add(f.group)
      result.push(f.group)
    }
  }
  if (result.length > 0) return result
  const colorwayGroups = Object.keys(project.colorways).sort()
  if (colorwayGroups.length > 0) return colorwayGroups
  return ["A"]
}

export function blockColors(project: Project, group: string): Record<string, string> {
  if (project.colorways?.[group]) {
    return { ...FALLBACK_COLORS, ...project.colorways[group] }
  }
  return GROUP_BLOCK_COLORS[group] ?? FALLBACK_COLORS
}

export function fabricSwatch(project: Project, fabric: Fabric): string {
  switch (fabric.role) {
    case "binding":     return fabric.hex ?? "#7a3a3a"
    case "backing":     return fabric.hex ?? "#9aa6b3"
    case "sashing":     return fabric.hex ?? SWATCH_SASHING
    case "cornerstone": return fabric.hex ?? SWATCH_CORNERSTONE
    case "setting":     return fabric.hex ?? "#bfb088"
    case null: {
      // Slot fabric: pull the swatch from the colorway palette assigned to
      // its group. Without a group there's no palette to consult, so fall
      // back to neutral gray.
      if (!fabric.slot || !fabric.group) return "#888"
      return blockColors(project, fabric.group)[fabric.slot] ?? "#888"
    }
    default: return assertNever(fabric)
  }
}

type DistFn = (r: number, c: number, n: number) => number
type RotFn  = (r: number, c: number, rows: number, cols: number) => number
type MaxGroupsFn = (rows: number, cols: number) => number
// `maxGroups` returns the largest number of distinct groups this distribution
// can place across a rows×cols grid. Colocated on DistEntry so a new
// distribution can't ship without declaring its capacity (per CLAUDE.md's
// "colocate variant data" rule).
interface DistEntry { label: string; group: DistFn; rotation?: RotFn; maxGroups: MaxGroupsFn }

export const DISTRIBUTIONS: Record<Distribution, DistEntry> = {
  diagonal:     { label: "Diagonal",     group: (r, c, n) => (r + c) % n,                 maxGroups: (r, c) => r + c - 1 },
  checkerboard: { label: "Checkerboard", group: (r, c, n) => ((r % 2) * 2 + (c % 2)) % n, maxGroups: () => 4 },
  rows:         { label: "Rows",         group: (r, c, n) => r % n,                        maxGroups: (r) => r },
  cols:         { label: "Columns",      group: (r, c, n) => c % n,                        maxGroups: (_, c) => c },
  dithered_cascade: {
    label: "Dithered Cascade",
    group:    (r, c, n) => (r + c) % n,  // base placement; the dither pass shuffles per-cell.
    maxGroups: (r, c) => r * c,
  },
  // Same group placement as `rows`; rotation alternates every other row so
  // Rail Fence's vertical rails flip to horizontal, producing the zig-zag fence.
  rail_fence_alternating: {
    label: "Rail Fence Alternating",
    group:    (r, c, n) => r % n,
    rotation: (r) => r % 2 === 0 ? 0 : 1,
    maxGroups: (r) => r,
  },
  // Diagonal group placement; rotation tiles a 2×2 pattern (0,1 / 3,2) so
  // each 2×2 cluster of blocks forms a larger spinning pinwheel.
  pinwheel_spin: {
    label: "Pinwheel Spin",
    group:    (r, c, n) => (r + c) % n,
    rotation: (r, c) => { const ri = r % 2, ci = c % 2; return ri === 0 ? ci : (ci === 0 ? 3 : 2) },
    maxGroups: (r, c) => r + c - 1,
  },
  // Diagonal group placement; rotation advances one quarter-turn per anti-diagonal.
  diagonal_spin: {
    label: "Diagonal Spin",
    group:    (r, c, n) => (r + c) % n,
    rotation: (r, c) => (r + c) % 4,
    maxGroups: (r, c) => r + c - 1,
  },
  // Log Cabin: diagonal group placement; rotation splits into four quadrants
  // (TL=0°, TR=90°, BL=270°, BR=180°) so light/dark fans into concentric diamond rings.
  barn_raising: {
    label: "Barn Raising",
    group:    (r, c, n) => (r + c) % n,
    rotation: (r, c, rows, cols) => ([[0, 1], [3, 2]] as const)[r < rows / 2 ? 0 : 1][c < cols / 2 ? 0 : 1],
    maxGroups: (r, c) => r + c - 1,
  },
  // Log Cabin: diagonal group placement; alternating columns rotate 180° so
  // the diagonal runs in opposite directions column-by-column (lightning bolts).
  streak_of_lightning: {
    label: "Streak of Lightning",
    group:    (r, c, n) => (r + c) % n,
    rotation: (r, c) => c % 2 === 0 ? 0 : 2,
    maxGroups: (r, c) => r + c - 1,
  },
}

export function distributionValues(): { value: Distribution; label: string }[] {
  return (Object.entries(DISTRIBUTIONS) as [Distribution, DistEntry][]).map(([value, e]) => ({ value, label: e.label }))
}

// Human label for a distribution. Single source of truth so cut plans, the
// transcript header, and the PDF all read the same name; falls back to the
// raw value if the input doesn't match a registered distribution so callers
// receiving untyped strings (e.g. tool outputs) degrade gracefully.
export function distributionLabel(d: string): string {
  return DISTRIBUTIONS[d as Distribution]?.label ?? d
}

// Largest distinct-group count this distribution can place across a
// rows×cols grid. Used by tools (`layout_set`) to surface the upper bound
// on useful colorways for a chosen layout.
export function maxDistinctGroupsFor(distribution: Distribution, rows: number, cols: number): number {
  return DISTRIBUTIONS[distribution].maxGroups(rows, cols)
}

// True iff the distribution emits any 90°/270° rotation. These rotations swap
// a non-square block's W and H, so the block doesn't tile cleanly under them.
// Sample a 4×4 grid — distribution rotation functions are periodic mod 2 or 4
// in both axes, so this catches every rotation value the function can emit.
export function distributionAxisSwapping(d: Distribution): boolean {
  const entry = DISTRIBUTIONS[d]
  if (!entry.rotation) return false
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      if (entry.rotation(r, c, 4, 4) % 2 !== 0) return true
    }
  }
  return false
}

// Build a synthetic Block whose `unit_inches` is scaled so that `schema`'s
// finished grid lands on the primary block's cell footprint. Both axes are
// computed and compared: a single `unit_inches` can match the primary cell
// only when the two schemas share an aspect ratio (cols/rows). Mismatched
// ratios — e.g. 3×2 Rail Fence primary paired with a 3×3 Nine-Patch alt —
// have no scalar that fits both axes, so we refuse instead of silently
// mis-sizing every cut piece downstream (cutBatches consumes this Block as
// the basis for cut math).
function scaledBlockTemplate(project: Project, schema: BlockSchema, pieceRoles: Record<string, string>): Block {
  const [primaryCols, primaryRows] = getSchema(project.block.style).grid_units
  const [altCols, altRows] = schema.grid_units
  const u = project.block.unit_inches
  const altUnitByCols = u * primaryCols / altCols
  const altUnitByRows = u * primaryRows / altRows
  if (Math.abs(altUnitByCols - altUnitByRows) > 1e-9) {
    throw new Error(
      `cannot fit schema '${schema.name}' (${altCols}×${altRows}) inside primary cell of '${project.block.style}' (${primaryCols}×${primaryRows}): aspect ratios mismatch — alternate/per-cell schemas must share the primary's cols:rows ratio.`
    )
  }
  return {
    style: schema.name,
    unit_inches: altUnitByCols,
    seam: project.block.seam,
    piece_roles: pieceRoles,
  }
}

// Synthetic Block for the layout's alternate schema (if any), with
// alternate_piece_roles overrides applied on top of schema defaults.
export function alternateBlock(project: Project): Block {
  const altSchemaName = project.layout.alternate_schema
  if (!altSchemaName) return project.block
  const altSchema = getSchema(altSchemaName)
  return scaledBlockTemplate(project, altSchema, {
    ...defaultPieceRolesFor(altSchema),
    ...(project.layout.alternate_piece_roles ?? {}),
  })
}

function cellSchemaName(project: Project, r: number, c: number): string {
  const override = project.layout.cell_schemas?.[`${r},${c}`]
  if (override) return override
  if (project.layout.alternate_schema && (r + c) % 2 === 1) return project.layout.alternate_schema
  return project.block.style
}

// Return the Block that should be rendered/cut for a given schema name.
// Primary schema → project.block (with its unit_inches and piece_roles).
// Alternate schema → alternateBlock() (which applies alternate_piece_roles).
// Per-cell schema → defaults with unit_inches scaled to match the primary block's footprint.
export function blockForSchema(project: Project, schemaName: string): Block {
  if (schemaName === project.block.style) return project.block
  if (schemaName === project.layout.alternate_schema) return alternateBlock(project)
  const schema = getSchema(schemaName)
  return scaledBlockTemplate(project, schema, defaultPieceRolesFor(schema))
}

export function groupGrid(project: Project): LayoutCell[][] {
  const rows = project.layout.rows
  const cols = project.layout.cols
  const groups = orderedGroups(project)
  const n = groups.length
  const dist = project.layout.distribution
  if (dist === "dithered_cascade") {
    return ditheredCascadeGrid(rows, cols, groups, project.layout.dither_swaps, project.layout.dither_seed, project)
  }

  const entry = DISTRIBUTIONS[dist]
  return Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (__, c) => ({
      group: groups[entry.group(r, c, n)],
      rotation: entry.rotation ? entry.rotation(r, c, rows, cols) : 0,
      schemaName: cellSchemaName(project, r, c),
    }))
  )
}

export function ditheredCascadeGrid(
  rows: number, cols: number, groups: string[], swaps: number, seed: number,
  project?: Project
): LayoutCell[][] {
  const n = groups.length
  const maxDiag = rows + cols - 2
  const grid: LayoutCell[][] = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (__, c) => ({
      group: groups[Math.min(Math.floor((r + c) * n / (maxDiag + 1)), n - 1)],
      rotation: 0,
      schemaName: project ? cellSchemaName(project, r, c) : DEFAULT_BLOCK_STYLE,
    }))
  )
  if (swaps <= 0) return grid

  // Seeded PRNG (simple LCG)
  let state = seed >>> 0
  const rand = () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0
    return state / 0x100000000
  }
  const allCells: [number, number][] = []
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) allCells.push([r, c])

  for (let i = 0; i < swaps; i++) {
    const idx1 = Math.floor(rand() * allCells.length)
    const [r1, c1] = allCells[idx1]
    const candidates: [number, number][] = []
    const weights: number[] = []
    for (let dr = -3; dr <= 3; dr++) {
      for (let dc = -3; dc <= 3; dc++) {
        if (dr === 0 && dc === 0) continue
        const r2 = r1 + dr, c2 = c1 + dc
        if (r2 >= 0 && r2 < rows && c2 >= 0 && c2 < cols) {
          candidates.push([r2, c2])
          weights.push(1.0 / (Math.abs(dr) + Math.abs(dc)) ** 2)
        }
      }
    }
    if (candidates.length === 0) continue
    const total = weights.reduce((s, w) => s + w, 0)
    let r = rand() * total
    let idx2 = 0
    for (let j = 0; j < weights.length; j++) {
      r -= weights[j]
      if (r <= 0) { idx2 = j; break }
    }
    const [r2, c2] = candidates[idx2]
    // Swap groups only — rotation stays attached to the cell, not the group.
    const tmp = grid[r1][c1].group
    grid[r1][c1] = { ...grid[r1][c1], group: grid[r2][c2].group }
    grid[r2][c2] = { ...grid[r2][c2], group: tmp }
  }
  return grid
}

// ---------------------------------------------------------------------------
// Shared geometry layer — drives both the SVG and PDF renderers
// ---------------------------------------------------------------------------

// Per-cell geometry descriptor. `transform` is an SVG transform attribute
// value (works identically for SVG string generation and react-pdf <G>).
// Sash rects are null when there is no sashing or the cell is at an edge.
export interface CellDescriptor {
  r: number
  c: number
  group: string
  schemaName: string
  transform: string
  sashRight: { x: number; y: number; w: number; h: number } | null
  sashBelow: { x: number; y: number; w: number; h: number } | null
  corner:    { x: number; y: number; w: number; h: number } | null
}

// On-point setting-triangle polygon, in canvas pixel coordinates. `kind`
// distinguishes the 2(R-1)+2(C-1) side triangles (one between each pair of
// adjacent edge blocks, long edge on the canvas perimeter) from the 4 corner
// triangles (one at each canvas corner, two legs on the perimeter). Renderers
// fill both with the same color today; the split exists so a future product
// call to support distinct fabrics for sides vs. corners is a one-line
// dispatch on `kind`. Empty for straight-set layouts. (LOOM_013 Facet A.)
export interface OuterTriangle {
  kind: "side" | "corner"
  points: Array<[number, number]>
}

// Border rectangle in canvas-absolute pixels. On-point projects with a
// non-zero `border_h_inches` or `border_v_inches` emit up to 4 border rects
// (top, bottom, left, right) framing the square pieced AABB; renderers paint
// them with the setting fabric color. Empty for straight set or zero borders.
export interface BorderRect {
  x: number
  y: number
  w: number
  h: number
}

export interface QuiltLayout {
  canvasW: number
  canvasH: number
  onPoint: boolean
  scale: number    // px per finished inch
  cellWPx: number  // cell width: blockW × scale (or max-cell when rotation+non-square)
  cellHPx: number  // cell height: blockH × scale (or max-cell when rotation+non-square)
  sashPx: number
  cells: CellDescriptor[]
  outerTriangles: OuterTriangle[]
  borders: BorderRect[]
}

// Pure geometry: where every block cell lives, what rotation it carries, and
// what sashing strips surround it. Both renderers (SVG string and react-pdf)
// consume this descriptor list so a new layout type only needs one change here.
export function computeQuiltLayout(project: Project, cellPx: number): QuiltLayout {
  const rows = project.layout.rows
  const cols = project.layout.cols
  const [blockW, blockH] = blockFinishedInches(project.block)
  const grid = groupGrid(project)
  // Per-axis cell dims so non-square blocks tile edge-to-edge. Fall back to a
  // square max-cell when any cell rotates 90°/270° on a non-square block — the
  // rotated footprint swaps axes and would otherwise overflow.
  const axisSwapping = grid.some((row) => row.some((cell) => cell.rotation % 2 !== 0))
  const useMaxCell = axisSwapping && blockW !== blockH
  const cellWInches = useMaxCell ? Math.max(blockW, blockH) : blockW
  const cellHInches = useMaxCell ? Math.max(blockW, blockH) : blockH
  const scale = cellPx / Math.max(blockW, blockH)
  const sashW = project.layout.sashing_width ?? 0
  const sashPx = sashW > 0 ? Math.max(2, Math.round(scale * sashW)) : 0
  const cellWPx = cellWInches * scale
  const cellHPx = cellHInches * scale
  const blockWPx = blockW * scale
  const blockHPx = blockH * scale
  const innerW = cols * cellWPx + (cols - 1) * sashPx
  const innerH = rows * cellHPx + (rows - 1) * sashPx
  const onPoint = project.layout.setting === "on_point" && blockW === blockH
  const blockDiag = cellWPx * Math.SQRT2
  // Canonical on-point: blocks tile a rotated lattice edge-to-edge with
  // pitch = half-diagonal between adjacent block centers. The pieced AABB
  // is a square — both axes equal (rows+cols)*pitch — regardless of M, N.
  // The lattice forms a tilted diamond inscribed in this square; the four
  // canvas-corner regions outside the diamond are setting fabric.
  // Optional borders extend the canvas around the AABB into a rectangular
  // finished blanket — borderHPx applies to left+right, borderVPx to top+bottom.
  const pitch = blockDiag / 2
  const canvasSquareSide = (rows + cols) * pitch
  const borderHPx = onPoint ? (project.layout.border_h_inches ?? 0) * scale : 0
  const borderVPx = onPoint ? (project.layout.border_v_inches ?? 0) * scale : 0
  const canvasW = onPoint ? canvasSquareSide + 2 * borderHPx : innerW
  const canvasH = onPoint ? canvasSquareSide + 2 * borderVPx : innerH
  const half = cellWPx / 2

  const cells: CellDescriptor[] = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = grid[r][c]
      const rot = ((cell.rotation % 4) + 4) % 4
      let transform: string
      let sashRight: CellDescriptor["sashRight"] = null
      let sashBelow: CellDescriptor["sashBelow"] = null
      let corner:    CellDescriptor["corner"]    = null

      if (onPoint) {
        // Per-block placement: block (r, c) center at
        // ((c-r+rows)*pitch, (c+r+1)*pitch) within the AABB; offset by
        // (borderHPx, borderVPx) when borders extend the canvas. Adjacent
        // blocks share full edges. Distribution rotations compose by adding
        // to the base 45°.
        const cx = (c - r + rows) * pitch + borderHPx
        const cy = (c + r + 1) * pitch + borderVPx
        const totalRot = 45 + rot * 90
        transform = `translate(${cx},${cy}) rotate(${totalRot}) translate(${-half},${-half})`
      } else {
        const ox = c * (cellWPx + sashPx)
        const oy = r * (cellHPx + sashPx)
        const paintedW = rot % 2 === 0 ? blockWPx : blockHPx
        const paintedH = rot % 2 === 0 ? blockHPx : blockWPx
        const padX = (cellWPx - paintedW) / 2
        const padY = (cellHPx - paintedH) / 2
        let dx = 0, dy = 0
        if (rot === 1)      { dx = blockHPx }
        else if (rot === 2) { dx = blockWPx; dy = blockHPx }
        else if (rot === 3) { dy = blockWPx }
        transform = rot === 0
          ? `translate(${ox + padX},${oy + padY})`
          : `translate(${ox + padX + dx},${oy + padY + dy}) rotate(${rot * 90})`

        if (sashPx > 0) {
          if (c < cols - 1) sashRight = { x: ox + cellWPx, y: oy,         w: sashPx, h: cellHPx }
          if (r < rows - 1) sashBelow = { x: ox,           y: oy + cellHPx, w: cellWPx, h: sashPx }
          if (c < cols - 1 && r < rows - 1) corner = { x: ox + cellWPx, y: oy + cellHPx, w: sashPx, h: sashPx }
        }
      }

      cells.push({ r, c, group: cell.group, schemaName: cell.schemaName, transform, sashRight, sashBelow, corner })
    }
  }

  // Canonical on-point: the rotated lattice tiles a diamond inscribed in
  // the square canvas. The four canvas corners (outside the diamond) are
  // filled by 4 large setting triangles — one per canvas corner. Each has
  // two legs along the canvas perimeter (kept on grain) and a hypotenuse
  // along one of the diamond's outer edges.
  //
  // Diamond vertices in canvas: top (rows*pitch, 0), right (sideTotal,
  // cols*pitch), bottom (cols*pitch, sideTotal), left (0, rows*pitch),
  // where sideTotal = canvasSquareSide. For square M=N all four corners
  // are congruent isoceles right triangles; for rectangular M≠N two pairs
  // differ in size.
  //
  // Note: `settingTrianglePlan` in geometry.ts was sized for the LOOM_016
  // corner-touching regression and over-counts triangles for this canonical
  // layout. Reconciling the cut math with this rendering is REC_007 step 3.
  const outerTriangles: OuterTriangle[] = []
  if (onPoint) {
    // AABB is offset by (borderHPx, borderVPx) inside the canvas; corner
    // triangle vertices are expressed in canvas-absolute coordinates.
    const x0 = borderHPx
    const y0 = borderVPx
    const x1 = x0 + canvasSquareSide
    const y1 = y0 + canvasSquareSide
    outerTriangles.push({
      kind: "corner",
      points: [[x0, y0], [x0 + rows * pitch, y0], [x0, y0 + rows * pitch]],
    })
    outerTriangles.push({
      kind: "corner",
      points: [[x1, y0], [x0 + rows * pitch, y0], [x1, y0 + cols * pitch]],
    })
    outerTriangles.push({
      kind: "corner",
      points: [[x1, y1], [x1, y0 + cols * pitch], [x0 + cols * pitch, y1]],
    })
    outerTriangles.push({
      kind: "corner",
      points: [[x0, y1], [x0, y0 + rows * pitch], [x0 + cols * pitch, y1]],
    })
  }

  // Border rectangles in canvas-absolute coordinates. Top/bottom span the
  // full canvas width; left/right span the inner height between them.
  // Empty when borders are zero or under straight set.
  const borders: BorderRect[] = []
  if (onPoint) {
    if (borderVPx > 0) {
      borders.push({ x: 0, y: 0, w: canvasW, h: borderVPx })
      borders.push({ x: 0, y: canvasH - borderVPx, w: canvasW, h: borderVPx })
    }
    if (borderHPx > 0) {
      borders.push({ x: 0, y: borderVPx, w: borderHPx, h: canvasH - 2 * borderVPx })
      borders.push({ x: canvasW - borderHPx, y: borderVPx, w: borderHPx, h: canvasH - 2 * borderVPx })
    }
  }

  return { canvasW, canvasH, onPoint, scale, cellWPx, cellHPx, sashPx, cells, outerTriangles, borders }
}

export function layoutSvg(project: Project, cellPx = 18): string {
  const rows = project.layout.rows
  const cols = project.layout.cols
  if (rows <= 0 || cols <= 0) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"></svg>`
  }
  const groups = orderedGroups(project)
  const grid = groupGrid(project)
  const [, blockSize] = blockFinishedInches(project.block)
  const sashW = project.layout.sashing_width ?? 0
  const sashPx = sashW > 0 ? Math.max(1, Math.round(cellPx * sashW / blockSize)) : 0
  const sashFabric = findFabricByRole(project, "sashing")
  const csFabric = findFabricByRole(project, "cornerstone")
  const SASH_COLOR = sashFabric ? fabricSwatch(project, sashFabric) : SWATCH_SASHING
  const CORNER_COLOR = csFabric ? fabricSwatch(project, csFabric) : SASH_COLOR
  const w = cols * cellPx + (cols - 1) * sashPx
  const h = rows * cellPx + (rows - 1) * sashPx
  const legendW = 90

  const rects: string[] = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const g = grid[r][c].group
      const color = blockColors(project, g).background ?? GROUP_COLORS[g] ?? "#ccc"
      const x = c * (cellPx + sashPx)
      const y = r * (cellPx + sashPx)
      rects.push(
        `<rect x="${x}" y="${y}" width="${cellPx}" height="${cellPx}" fill="${color}" stroke="#fff" stroke-width="0.5"/>`
      )
      if (sashPx > 0) {
        if (c < cols - 1)
          rects.push(`<rect x="${x + cellPx}" y="${y}" width="${sashPx}" height="${cellPx}" fill="${SASH_COLOR}"/>`)
        if (r < rows - 1)
          rects.push(`<rect x="${x}" y="${y + cellPx}" width="${cellPx}" height="${sashPx}" fill="${SASH_COLOR}"/>`)
        if (c < cols - 1 && r < rows - 1)
          rects.push(`<rect x="${x + cellPx}" y="${y + cellPx}" width="${sashPx}" height="${sashPx}" fill="${CORNER_COLOR}"/>`)
      }
    }
  }

  const legend: string[] = []
  for (let i = 0; i < groups.length; i++) {
    const g = groups[i]
    const ly = i * 20
    const color = blockColors(project, g).background ?? GROUP_COLORS[g] ?? "#ccc"
    legend.push(
      `<rect x="${w + 10}" y="${ly + 1}" width="13" height="13" fill="${color}" stroke="#888" stroke-width="0.5"/>`,
      `<text x="${w + 28}" y="${ly + 11}" font-size="11" font-family="Helvetica,sans-serif" fill="#333">Group ${g}</text>`
    )
  }

  const svgH = Math.max(h, groups.length * 20)
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w + legendW}" height="${svgH}" viewBox="0 0 ${w + legendW} ${svgH}">${rects.join("")}${legend.join("")}</svg>`
}

export function fullQuiltSvg(project: Project, blockPx = 48, mode: "color" | "value" = "color"): string {
  const rows = project.layout.rows
  const cols = project.layout.cols
  if (rows <= 0 || cols <= 0) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"></svg>`
  }
  const groups = orderedGroups(project)
  const layout = computeQuiltLayout(project, blockPx)
  const { canvasW, canvasH, onPoint, scale } = layout

  const sashFabric = findFabricByRole(project, "sashing")
  const csFabric = findFabricByRole(project, "cornerstone")
  const SASH_COLOR_RAW = sashFabric ? fabricSwatch(project, sashFabric) : SWATCH_SASHING
  const CORNER_COLOR_RAW = csFabric ? fabricSwatch(project, csFabric) : SASH_COLOR_RAW
  const SASH_COLOR = mode === "value" ? hexToValueHex(SASH_COLOR_RAW) : SASH_COLOR_RAW
  const CORNER_COLOR = mode === "value" ? hexToValueHex(CORNER_COLOR_RAW) : CORNER_COLOR_RAW

  // Discover all unique schemas in the layout from the pre-computed cell list
  // (includes primary, alternate_schema, and per-cell cell_schemas overrides).
  // Pre-render each (schemaName, group) pair once; blockForSchema handles
  // unit_inches scaling so every schema fills the same cellPx footprint.
  const uniqueSchemas = new Set<string>([project.block.style])
  for (const cell of layout.cells) uniqueSchemas.add(cell.schemaName)

  const schemaGroupInner: Record<string, Record<string, string>> = {}
  for (const sName of uniqueSchemas) {
    const sBlock = blockForSchema(project, sName)
    schemaGroupInner[sName] = {}
    for (const g of groups) {
      const rawColors = blockColors(project, g)
      const colors = mode === "value" ? toValueColors(rawColors) : rawColors
      const svg = renderBlock(sBlock, scale, colors)
      const start = svg.indexOf(">") + 1
      const end = svg.lastIndexOf("</svg>")
      schemaGroupInner[sName][g] = svg.slice(start, end).trim()
    }
  }

  const parts: string[] = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${canvasW}" height="${canvasH}" viewBox="0 0 ${canvasW} ${canvasH}">`,
  ]

  if (onPoint) {
    const settingColorRaw = onPointSettingColor(project, groups)
    const settingColor = mode === "value" ? hexToValueHex(settingColorRaw) : settingColorRaw
    // Borders frame the pieced AABB; paint them with the same setting fabric.
    for (const b of layout.borders) {
      parts.push(`<rect x="${b.x}" y="${b.y}" width="${b.w}" height="${b.h}" fill="${settingColor}"/>`)
    }
    for (const tri of layout.outerTriangles) {
      const pts = tri.points.map(([x, y]) => `${x},${y}`).join(" ")
      parts.push(`<polygon points="${pts}" fill="${settingColor}"/>`)
    }
  }

  for (const cell of layout.cells) {
    const cellInner = schemaGroupInner[cell.schemaName]?.[cell.group]
      ?? schemaGroupInner[project.block.style]?.[cell.group] ?? ""
    parts.push(`<g transform="${cell.transform}">${cellInner}</g>`)
    if (cell.sashRight) {
      const { x, y, w, h } = cell.sashRight
      parts.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${SASH_COLOR}"/>`)
    }
    if (cell.sashBelow) {
      const { x, y, w, h } = cell.sashBelow
      parts.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${SASH_COLOR}"/>`)
    }
    if (cell.corner) {
      const { x, y, w, h } = cell.corner
      parts.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${CORNER_COLOR}"/>`)
    }
  }

  parts.push(`</svg>`)
  return parts.join("")
}

// Setting-triangle fill color. Prefers a dedicated `setting` fabric, falls
// back to group A's background. Pure rendering helper — the cut planner
// (`settingTrianglePlan`) makes the same fabric choice for billing.
function onPointSettingColor(project: Project, groups: string[]): string {
  const setting = findFabricByRole(project, "setting")
  if (setting) return fabricSwatch(project, setting)
  if (groups.length > 0) {
    return blockColors(project, groups[0]).background ?? DEFAULT_COLORWAY.background
  }
  return DEFAULT_COLORWAY.background
}
